import React from 'react'

import { DateTime } from 'luxon'
import { Link } from 'react-router-dom'

import type { Entity, IsoDate } from '@/types/dashboard-types'
import { isEntity } from '@/types/dashboard-types'

import { toQueryParam } from './queryUtils'
import { type DrilldownLink, isDrilldownLink } from './types'

/* Given a DrilldownLink type, create the appropriate URL to use for
 * the <Link> component so the drilldown will be loaded when the user
 * clicks the link. The path needs to match the @card/insights
 * route so it will load inline, not do a page refresh.
 */
const buildDrilldownUrl = (drilldownLink: DrilldownLink): string => {
  const query = Object.entries(drilldownLink.queryParams || {})
    .map(([key, value]) => `${key}=${toQueryParam(value)}`)
    .join('&')
  const path = `/drilldown/${drilldownLink.slug}?parentSlug=${drilldownLink.parentSlug}&${query}`
  return path
}

/* Format a parameter or display item for the user. */
export const format = (
  value: undefined | null | number | Date | string | Entity | DrilldownLink,
  location?: string
): string | React.JSX.Element => {
  if (typeof value === 'undefined') {
    return ''
  }

  if (isDrilldownLink(value)) {
    const displayValueText = isEntity(value.displayValue) ? value.displayValue.name : value.displayValue
    return (
      <Link to={buildDrilldownUrl(value)} state={{ backgroundLocation: location }}>
        {displayValueText}
      </Link>
    )
  }
  if (isEntity(value)) {
    return value.name
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toLocaleString()
  }
  if (typeof value === 'number') {
    return new Intl.NumberFormat().format(value)
  }
  if (!value) {
    return ''
  }
  return value
}

export const duration = (date: Date | IsoDate | undefined | null): string => {
  if (!date) {
    return ''
  }
  const d = typeof date == 'string' ? DateTime.fromISO(date).toJSDate() : date
  const full = DateTime.now().diff(DateTime.fromJSDate(d)).rescale()
  return full.toHuman({ unitDisplay: 'short' }).split(',').slice(0, 2).join('')
}

export const calculateTimeDifference = (dateA: Date, dateB?: Date | null) => {
  if (!dateB) {
    return undefined
  }

  const timeA = DateTime.fromJSDate(dateA)
  const timeB = DateTime.fromJSDate(dateB)

  const diff = timeB.diff(timeA).rescale()

  return diff.toHuman({ unitDisplay: 'narrow' }).split(',').slice(0, 2).join('')
}

export const interval = (from: Date, to: Date | undefined): string => {
  if (!from || !to) {
    return ''
  }
  const full = DateTime.fromJSDate(to).diff(DateTime.fromJSDate(from)).rescale()
  return full.toHuman({ unitDisplay: 'short' }).split(',').slice(0, 2).join('')
}

export const validateDateWithin30Days = (dateString?: string): boolean | string => {
  if (!dateString) {
    return true
  }

  const today = DateTime.now().startOf('day')
  const thirtyDaysAgo = today.minus({ days: 30 })
  const userDate = DateTime.fromISO(dateString).startOf('day')

  if (!userDate.isValid) {
    return 'Invalid date format'
  }
  if (userDate < thirtyDaysAgo) {
    return 'Data is limited to the last 30 days'
  }
  return true
}

export const validateDateRangeWithin30Days = (from?: string, to?: string): boolean | string => {
  const fromResult = validateDateWithin30Days(from)
  if (fromResult !== true) {
    return fromResult
  }

  const toResult = validateDateWithin30Days(to)
  if (toResult !== true) {
    return toResult
  }
  return true
}

export const validateDateWithin13Months = (dateString?: string): boolean | string => {
  if (!dateString) {
    return true
  }

  const today = DateTime.now().startOf('day')
  const thirteenMonthsAgo = today.minus({ months: 13 })
  const userDate =
    typeof dateString === 'string'
      ? DateTime.fromISO(dateString).startOf('day')
      : DateTime.fromJSDate(dateString).startOf('day')

  if (!userDate.isValid) {
    return ' Invalid date format'
  }
  if (userDate < thirteenMonthsAgo) {
    return 'Date is limited to the last 13 months'
  }
  return true
}

export const validateDateRangeWithin13Months = (from?: string, to?: string): boolean | string => {
  const fromResult = validateDateWithin13Months(from)
  if (fromResult !== true) {
    return fromResult
  }

  const toResult = validateDateWithin13Months(to)
  if (toResult !== true) {
    return toResult
  }
  return true
}
